import { Dispatch, useEffect } from 'react';
import { toast } from "sonner";
import { useLocation, useNavigate } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import type {
  AccountRegistrationStatus,
  HonoraryFlagValue,
  MeCurrentMembershipDto,
  SupplierOnboardingStatusDto,
} from '@paladin-monorepo/common';
import { userNeedsSupplierOnboarding } from './supplierOnboarding';
import { userNeedsRegistrationGate } from './registrationApproval';

import API, { API_URL } from './api';
import { invalidateAllQueries } from './queryCache';
import { trackEvent } from './analytics';
import { destroyProductFruits } from './productFruits';

interface AuthHookProps {
    middleware?: 'guest' | 'auth';
    redirectTo?: string;
    tmp: string;
}

interface LoginProps {
    values: {
        email: string;
        password: string;
    };
    setError: Dispatch<any>;
}

interface VerifyProps {
    values: {
        email: string;
        token: string;
    };
    setError: Dispatch<any>;
}

interface ForgotPasswordProps {
    email: string;
}

interface RestPasswordProps {
    email: string;
    password: string;
    password_confirmation: string;
}

export interface MembershipInfo {
    id: string;
    accountId: string;
    accountName?: string;
    accountType?: string;
    role: string;
}

export interface UserProps {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    /** Pending self-service email change awaiting verification (claim account flow). */
    pendingEmail?: string | null;
    roles: string[];
    sia: boolean;
    fpr?: string;
    role: string;
    systemRole?: string | null;
    currentAccountId?: string | null;
    currentMembership?: MeCurrentMembershipDto | null;
    account?: {
        id: string;
        accountType: string;
        defaultCurrency?: string;
        honoraryFlag?: HonoraryFlagValue | null;
        prince2Enabled?: boolean;
        bedrockEnabled?: boolean;
        registrationStatus?: AccountRegistrationStatus;
        approvedAt?: string | null;
        rejectedAt?: string | null;
        party: {
            name: string;
        };
    };
    party: {
        firstName: string;
    };
    memberships?: MembershipInfo[];
    supplierOnboarding?: SupplierOnboardingStatusDto;
    /** Demo mode (GET /me) - only present for SUPERADMIN/SUPPORT when DEMO_MODE=true */
    demoMode?: boolean;
    impersonatorId?: string;
    impersonatorEmail?: string;
}

function getAuthErrorStatus(error: unknown): number | undefined {
    if (axios.isAxiosError(error)) {
        return error.response?.status;
    }
    return undefined;
}

export const useAuth = ({ middleware, redirectTo, tmp }: AuthHookProps) => {
    const navigate = useNavigate();
    const location = useLocation();
    const queryClient = useQueryClient();

    const {
        data: user,
        isLoading,
        error,
    } = useQuery<UserProps | null>({
        queryKey: ['/me'],
        queryFn: async () => {
            try {
                const res = await API.get('/me');
                return res.data;
            } catch (queryError) {
                const status = getAuthErrorStatus(queryError);
                // Treat unauthenticated as empty session (do not throw - stale cache
                // would otherwise keep the previous user and break guest routes).
                if (status === 401) {
                    queryClient.setQueryData(['/me'], null);
                    return null;
                }
                if (axios.isAxiosError(queryError) && queryError.code === 'ECONNABORTED') {
                    return null;
                }
                throw queryError;
            }
        },
        retry: (failureCount, queryError) => {
            const status = getAuthErrorStatus(queryError);
            if (status === 401 || status === 503) {
                return false;
            }
            return failureCount < 2;
        },
    });

    const refetchUser = async () => {
        await queryClient.refetchQueries({ queryKey: ['/me'] });
    };

    const csrf = () => API.get('/auth/csrf-token');

    /** Full-page redirect to LinkedIn - covers both sign-up (Story 1) and login (Story 2). */
    const loginWithLinkedIn = () => {
        window.location.href = `${API_URL}/auth/linkedin`;
    };

    /** Full-page redirect to LinkedIn to pair it with the already-authenticated account (Story 3). */
    const connectLinkedIn = () => {
        window.location.href = `${API_URL}/auth/linkedin/connect`;
    };

    const loginWithGoogle = () => {
        window.location.href = `${API_URL}/auth/google`;
    };

    const connectGoogle = () => {
        window.location.href = `${API_URL}/auth/google/connect`;
    };

    const login = async ({ values, setError }: LoginProps) => {
        setError(false);

        return API.post('/auth/login', {
            username: values.email,
            password: values.password,
        })
            .then(async (res) => {
                const sia = res?.data?.user?.sia;
                trackEvent('login', { method: 'password', sia });

                await invalidateAllQueries(queryClient);
                const refreshed = queryClient.getQueryData<UserProps>(['/me']);
                if (userNeedsRegistrationGate(refreshed)) {
                    navigate('/waiting-list');
                } else {
                    navigate(
                        userNeedsSupplierOnboarding(refreshed)
                            ? '/onboarding'
                            : '/monomo-ai',
                    );
                }

                return res;
            })
            .catch((error) => {
                trackEvent('login_failed', { method: 'password' });
                setError(error.response.data?.message);

                throw error;
            });
    };

    const loginNonce = async ({ values, setError }: VerifyProps) => {
        setError(false);
        await csrf();

        return API.post('/auth/nonce', values)
            .then(async (res) => {
                const sia = res?.data?.user?.sia;
                trackEvent('login', { method: 'nonce', sia });
                await invalidateAllQueries(queryClient);

                return res;
            })
            .catch((error) => {
                trackEvent('login_failed', { method: 'nonce' });
                setError(error.response.data?.message);

                throw error;
            });
    };

    const forgotPassword = async ({ email }: ForgotPasswordProps) => {
        await csrf();

        return API.post('/forgot-password', { email })
            .then((res) => {
                toast(res?.data?.status);
            })
            .catch((error) => {
                if (error.response.status !== 422) throw error;
            });
    };

    const resetPassword = async ({
        email,
        password,
        password_confirmation,
    }: RestPasswordProps) => {
        await csrf();

        return API.post('/reset-password', {
            email,
            password,
            password_confirmation,
        })
            .then((res) => {
                toast(res?.data?.status);
            })
            .catch((error) => {
                if (error.response.status !== 422) throw error;
            });
    };

    const logout = async (options?: { redirectTo?: string }) => {
        const currentUser = queryClient.getQueryData<UserProps>(['/me']);
        const isImpersonating = Boolean(currentUser?.impersonatorId);

        return API.post(isImpersonating ? '/auth/stop-impersonation' : '/auth/logout')
            .then(async () => {
                await invalidateAllQueries(queryClient);
                if (!isImpersonating) {
                    destroyProductFruits();
                }
                if (!isImpersonating) {
                    queryClient.setQueryData(['/me'], null);
                }
                navigate(isImpersonating ? '/admin' : (options?.redirectTo ?? '/auth/login'));
            })
            .catch(async (error) => {
                if (error?.response?.status === 401) {
                    return refetchUser();
                }

                throw error;
            });
    };

    useEffect(() => {
        if (!middleware || isLoading) return;

        if (middleware === 'guest' && user) {
            navigate(redirectTo ?? '/');
            return;
        }

        const status = getAuthErrorStatus(error);
        if (status === 503) {
            navigate('/maintenance?ref=' + encodeURIComponent(location.pathname));
            return;
        }

        if (middleware === 'auth' && !user) {
            navigate(redirectTo ?? '/auth/login');
        }
    }, [middleware, user, error, redirectTo, isLoading, navigate, location.pathname]);

    return {
        user,
        error,
        csrf,
        isLoading,
        login,
        loginNonce,
        loginWithLinkedIn,
        connectLinkedIn,
        loginWithGoogle,
        connectGoogle,
        forgotPassword,
        resetPassword,
        logout,
        refetchUser,
    };
};
